import { useParams, Link } from 'react-router-dom'
import { useGlossary } from '../context/GlossaryContext.jsx'
import { relatedSlugs } from '../data/glossary.js'
import { withBrand } from '../lib/seoTitle.js'
import Seo from '../components/Seo.jsx'
import LastUpdated, { CONTENT_UPDATED } from '../components/LastUpdated.jsx'

const SITE = 'https://calculatemypeptides.com'
const trim = (s, n = 160) => (s.length <= n ? s : s.slice(0, n - 1).replace(/\s+\S*$/, '') + '…')

export default function GlossaryTerm() {
  const { slug } = useParams()
  const { glossary, getTerm } = useGlossary()
  const t = getTerm(slug)

  if (!t) {
    return (
      <main className="page">
        <div className="container center" style={{ padding: '60px 0' }}>
          <h1 className="section-title">Term not found</h1>
          <Link to="/glossary" className="btn btn-primary">Back to glossary</Link>
        </div>
      </main>
    )
  }

  const idx = glossary.findIndex((x) => x.slug === slug)
  const prev = idx > 0 ? glossary[idx - 1] : null
  const next = idx >= 0 && idx < glossary.length - 1 ? glossary[idx + 1] : null

  // Semantic related terms (topic clusters), padded with alphabetical neighbours.
  const relSet = relatedSlugs(slug)
  const byTopic = relSet.map((s) => glossary.find((x) => x.slug === s)).filter(Boolean)
  const neighbours = Array.from({ length: 10 }, (_, i) => glossary[(idx + 1 + i) % glossary.length])
    .filter((x) => x && x.slug !== slug && !relSet.includes(x.slug))
  const related = [...byTopic, ...neighbours].slice(0, 10)

  // Only show the "open the calculator" callout on terms that actually relate to
  // the reconstitution math — not on pure concept terms (agonist, receptor, etc.).
  const calcRelevant = /reconstitut|concentrat|\bunits?\b|syringe|\bdos|bacteriostatic|dilut|\bvial|milligram|microgram|u-?100|titrat|half-life|frontload/i.test(`${t.term} ${t.def}`)

  return (
    <main className="page">
      <Seo
        title={withBrand(`${t.term} — Meaning & Definition`)}
        description={trim(`${t.term}: ${t.def} A plain-English term from the CalculateMyPeptides peptide dosing and reconstitution glossary.`)}
        image="/og/glossary.png"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Glossary', path: '/glossary' },
          { name: t.term, path: `/glossary/${slug}` },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'DefinedTerm',
          name: t.term,
          ...(t.aka ? { alternateName: t.aka.split(',').map((s) => s.trim()) } : {}),
          description: t.def,
          inDefinedTermSet: `${SITE}/glossary`,
          url: `${SITE}/glossary/${slug}`,
          dateModified: CONTENT_UPDATED,
        }}
      />
      <div className="container page-narrow">
        <nav className="crumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">›</span>
          <Link to="/glossary">Glossary</Link>
          <span aria-hidden="true">›</span>
          <span className="crumbs-current">{t.term}</span>
        </nav>

        <span className="pill blue" style={{ marginBottom: 10 }}>Glossary</span>
        <h1 className="section-title">{t.term}</h1>
        {t.aka && <p className="glossary-aka" style={{ marginTop: 0 }}>Also known as “{t.aka}”</p>}
        <LastUpdated />

        <p className="term-def">{t.def}</p>
        {t.long && <p className="term-long">{t.long}</p>}

        {calcRelevant && (
          <div className="guide-callout" style={{ margin: '18px 0' }}>
            🧮 <span>Put it into practice — <Link to="/calculator"><strong>open the peptide calculator →</strong></Link></span>
          </div>
        )}

        <section className="pep-section">
          <h2 className="pep-h2">Keep reading</h2>
          <div className="pep-related">
            <Link to="/peptide-dosage-guide" className="pep-related-card">
              <span className="pep-related-body">
                <span className="pep-related-name">Peptide dosage guide</span>
                <span className="pep-related-desc">The reconstitution formula and a worked example.</span>
              </span>
            </Link>
            <Link to="/peptides" className="pep-related-card">
              <span className="pep-related-body">
                <span className="pep-related-name">Peptide library</span>
                <span className="pep-related-desc">Profiles, dosing and reconstitution for 100+ compounds.</span>
              </span>
            </Link>
          </div>
        </section>

        <section className="pep-section">
          <h2 className="pep-h2">Related terms</h2>
          <div className="chart-chips">
            {related.map((r) => (
              <Link key={r.slug} to={`/glossary/${r.slug}`} className="chart-chip">{r.term}</Link>
            ))}
          </div>
        </section>

        {(prev || next) && (
          <nav className="term-nav" aria-label="Adjacent terms">
            {prev ? <Link to={`/glossary/${prev.slug}`} className="term-nav-link">← {prev.term}</Link> : <span />}
            {next ? <Link to={`/glossary/${next.slug}`} className="term-nav-link term-nav-next">{next.term} →</Link> : <span />}
          </nav>
        )}

        <p className="muted small" style={{ marginTop: 20, maxWidth: '68ch' }}>
          Educational reference only — not medical advice. Always follow the guidance of a qualified healthcare professional.
        </p>
      </div>
    </main>
  )
}
